import React, { useState, useEffect } from 'react'
import {
  Table, Card, Input, Select, DatePicker, Button, Space, Tag, Drawer, Typography, message
} from 'antd'
import { SearchOutlined, ExportOutlined, EyeOutlined } from '@ant-design/icons'
import { conversationApi } from '@/services/api'
import dayjs, { Dayjs } from 'dayjs'

const { RangePicker } = DatePicker
const { Option } = Select
const { Text, Paragraph } = Typography

interface Conversation {
  id: number
  user_id: string
  chat_id: string
  model: string
  title: string
  message_count: number
  last_message: string
  total_tokens: number
  created_at: number
  updated_at: number
}

interface Message {
  id: number
  role: 'user' | 'assistant' | 'system'
  content: string
  tokens: number
  created_at: number
}

const ConversationsPage: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  })

  // 筛选条件
  const [filters, setFilters] = useState({
    userId: '',
    model: '',
    keyword: '',
    dateRange: null as [dayjs.Dayjs, dayjs.Dayjs] | null
  })

  // 会话详情抽屉
  const [detailVisible, setDetailVisible] = useState(false)
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [detailLoading, setDetailLoading] = useState(false)

  const fetchConversations = async (page = 1) => {
    try {
      setLoading(true)
      const params = {
        page,
        limit: pagination.pageSize,
        userId: filters.userId || undefined,
        model: filters.model || undefined,
        keyword: filters.keyword || undefined,
        startDate: filters.dateRange?.[0]?.valueOf(),
        endDate: filters.dateRange?.[1]?.valueOf()
      }

      const response = await conversationApi.getConversations(params)
      if (response.success) {
        setConversations(response.data || [])
        setPagination(prev => ({
          ...prev,
          current: page,
          total: response.pagination?.total || 0
        }))
      } else {
        message.error('获取会话列表失败')
      }
    } catch (error) {
      console.error('获取会话列表失败:', error)
      message.error('获取会话列表失败')
    } finally {
      setLoading(false)
    }
  }

  const fetchConversationDetail = async (conversation: Conversation) => {
    try {
      setDetailLoading(true)
      const response = await conversationApi.getConversationDetail(
        conversation.user_id,
        conversation.chat_id
      )
      if (response.success) {
        setMessages(response.data.messages)
        setSelectedConversation(conversation)
        setDetailVisible(true)
      } else {
        message.error('获取会话详情失败')
      }
    } catch (error) {
      console.error('获取会话详情失败:', error)
      message.error('获取会话详情失败')
    } finally {
      setDetailLoading(false)
    }
  }

  const handleExport = () => {
    const params = {
      userId: filters.userId || undefined,
      model: filters.model || undefined,
      keyword: filters.keyword || undefined,
      startDate: filters.dateRange?.[0]?.valueOf(),
      endDate: filters.dateRange?.[1]?.valueOf()
    }
    conversationApi.exportConversations(params)
  }

  useEffect(() => {
    fetchConversations()
  }, [filters])

  const columns = [
    {
      title: '用户ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 120,
      render: (text: string) => (
        <Text code>{text.substring(0, 8)}...</Text>
      )
    },
    {
      title: '会话ID',
      dataIndex: 'chat_id',
      key: 'chat_id',
      width: 120,
      render: (text: string) => (
        <Text code>{text.substring(0, 8)}...</Text>
      )
    },
    {
      title: '模型',
      dataIndex: 'model',
      key: 'model',
      width: 200,
      render: (text: string) => <Tag color="blue">{text}</Tag>
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true
    },
    {
      title: '消息数',
      dataIndex: 'message_count',
      key: 'message_count',
      width: 80,
      align: 'center' as const
    },
    {
      title: 'Token数',
      dataIndex: 'total_tokens',
      key: 'total_tokens',
      width: 100,
      align: 'right' as const,
      render: (value: number) => value?.toLocaleString() || 0
    },
    {
      title: '最后消息',
      dataIndex: 'last_message',
      key: 'last_message',
      ellipsis: true,
      render: (text: string) => (
        <Paragraph ellipsis={{ rows: 1 }} style={{ margin: 0 }}>
          {text}
        </Paragraph>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (timestamp: number) => dayjs(timestamp).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      render: (_: any, record: Conversation) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => fetchConversationDetail(record)}
        >
          查看
        </Button>
      )
    }
  ]

  return (
    <div style={{ padding: '0' }}>
      <Card>
        {/* 筛选条件 */}
        <Space wrap style={{ marginBottom: '16px' }}>
          <Input
            placeholder="用户ID"
            value={filters.userId}
            onChange={e => setFilters(prev => ({ ...prev, userId: e.target.value }))}
            style={{ width: 150 }}
          />
          <Select
            placeholder="选择模型"
            value={filters.model || undefined}
            onChange={value => setFilters(prev => ({ ...prev, model: value || '' }))}
            style={{ width: 200 }}
            allowClear
          >
            <Option value="anthropic/claude-3.7-sonnet">Claude 3.7 Sonnet</Option>
            <Option value="anthropic/claude-3.5-haiku">Claude 3.5 Haiku</Option>
            <Option value="openai/gpt-4">GPT-4</Option>
          </Select>
          <Input
            placeholder="搜索关键词"
            value={filters.keyword}
            onChange={e => setFilters(prev => ({ ...prev, keyword: e.target.value }))}
            style={{ width: 200 }}
            prefix={<SearchOutlined />}
          />
          <RangePicker
            value={filters.dateRange}
            onChange={value => setFilters(prev => ({ ...prev, dateRange: value as [Dayjs, Dayjs] | null }))}
            placeholder={['开始日期', '结束日期']}
          />
          <Button type="primary" icon={<ExportOutlined />} onClick={handleExport}>
            导出数据
          </Button>
        </Space>

        {/* 会话列表 */}
        <Table
          columns={columns}
          dataSource={conversations}
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
          }}
          onChange={(paginationInfo) => {
            fetchConversations(paginationInfo.current)
          }}
        />
      </Card>

      {/* 会话详情抽屉 */}
      <Drawer
        title={`会话详情 - ${selectedConversation?.title}`}
        placement="right"
        width={800}
        open={detailVisible}
        onClose={() => setDetailVisible(false)}
        loading={detailLoading}
      >
        {selectedConversation && (
          <div>
            {/* 会话信息 */}
            <Card size="small" style={{ marginBottom: '16px' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div><strong>用户ID:</strong> <Text code>{selectedConversation.user_id}</Text></div>
                <div><strong>会话ID:</strong> <Text code>{selectedConversation.chat_id}</Text></div>
                <div><strong>模型:</strong> <Tag color="blue">{selectedConversation.model}</Tag></div>
                <div><strong>消息数:</strong> {selectedConversation.message_count}</div>
                <div><strong>Token数:</strong> {selectedConversation.total_tokens?.toLocaleString() || 0}</div>
                <div><strong>创建时间:</strong> {dayjs(selectedConversation.created_at).format('YYYY-MM-DD HH:mm:ss')}</div>
              </Space>
            </Card>

            {/* 消息列表 */}
            <div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
              {messages.map((message) => (
                <Card
                  key={message.id}
                  size="small"
                  style={{ marginBottom: '8px' }}
                  title={
                    <Space>
                      <Tag color={message.role === 'user' ? 'green' : 'blue'}>
                        {message.role === 'user' ? '用户' : 'AI助手'}
                      </Tag>
                      <Text type="secondary">
                        {dayjs(message.created_at).format('HH:mm:ss')}
                      </Text>
                      {message.tokens > 0 && (
                        <Tag>{message.tokens} tokens</Tag>
                      )}
                    </Space>
                  }
                >
                  <Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                    {message.content}
                  </Paragraph>
                </Card>
              ))}
            </div>
          </div>
        )}
      </Drawer>
    </div>
  )
}

export default ConversationsPage
